import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, Check, Loader2, LogIn } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { QuizData } from "@/types/quiz";

interface ShareResultDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quizData: QuizData;
  answers: any[];
  score: number;
  correctAnswers: number;
  wrongAnswers: number;
  unanswered: number;
  timeTaken: number;
  userId?: string;
}

const ShareResultDialog = ({
  open,
  onOpenChange,
  quizData,
  answers,
  score,
  correctAnswers,
  wrongAnswers,
  unanswered,
  timeTaken,
  userId,
}: ShareResultDialogProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [shareLink, setShareLink] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  // Auto-generate share link when dialog opens
  useEffect(() => {
    if (open && userId && !hasGenerated && !shareLink && !isGenerating) {
      generateShareLink();
    }
  }, [open, userId, hasGenerated]);

  const generateShareLink = async () => {
    // Check if user is logged in
    if (!userId) {
      return;
    }

    // Prevent multiple generations
    if (hasGenerated || isGenerating) {
      return;
    }

    setIsGenerating(true);
    try {
      // First, save the quiz
      const { data: quiz, error: quizError } = await supabase
        .from("quizzes")
        .insert({
          user_id: userId,
          topic: quizData.settings.topic,
          difficulty: quizData.settings.difficulty,
          total_questions: quizData.settings.totalQuestions,
          timer_minutes: quizData.settings.timerMinutes,
          negative_marking: quizData.settings.negativeMarking,
          penalty: quizData.settings.penalty,
          questions_data: quizData.questions as any,
        })
        .select()
        .single();

      if (quizError) throw quizError;

      // Save the attempt
      await supabase
        .from("quiz_attempts")
        .insert({
          quiz_id: quiz.id,
          user_id: userId,
          score,
          total_questions: quizData.questions.length,
          correct_answers: correctAnswers,
          wrong_answers: wrongAnswers,
          unanswered,
          time_taken_seconds: timeTaken,
          answers_data: answers as any,
        });

      // Generate short token using database function
      const { data: tokenData, error: tokenError } = await supabase
        .rpc("generate_share_token");

      if (tokenError) throw tokenError;

      const shortCode = tokenData as string;

      // Create shared quiz entry
      const { error: shareError } = await supabase
        .from("shared_quizzes")
        .insert({
          quiz_id: quiz.id,
          share_token: shortCode,
        });

      if (shareError) throw shareError;

      const link = `${window.location.origin}/${shortCode}`;
      setShareLink(link);
      setHasGenerated(true);

      toast({
        title: "Share Link Generated!",
        description: "Your quiz is now ready to share",
      });
    } catch (error: any) {
      console.error("Error generating share link:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate share link",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast({
        title: "Link Copied!",
        description: "Share link copied to clipboard",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Copy Failed",
        description: "Failed to copy link to clipboard",
        variant: "destructive",
      });
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
    // Reset state when dialog closes for future use
    if (!newOpen) {
      // Keep the generated link if already generated
    }
  };

  // Show login prompt if user is not logged in
  if (!userId) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md mx-3 sm:mx-0 rounded-2xl">
          <DialogHeader className="space-y-3">
            <DialogTitle className="text-xl sm:text-2xl font-bold">Login Required</DialogTitle>
            <DialogDescription className="text-sm sm:text-base">
              You need to log in to share this quiz. Please sign in to generate a shareable link.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="rounded-xl bg-gradient-to-br from-primary/10 to-accent/10 p-4 sm:p-5 space-y-3 border border-border/50">
              <p className="text-sm sm:text-base font-semibold text-foreground">Why login?</p>
              <ul className="text-xs sm:text-sm text-foreground/80 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>Save your quizzes permanently</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>Share quizzes with friends and colleagues</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>Track your progress over time</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>Access your quizzes from anywhere</span>
                </li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => {
                  onOpenChange(false);
                  navigate("/auth?mode=signin");
                }}
                className="flex-1"
              >
                <LogIn className="mr-2 h-4 w-4" />
                Sign In
              </Button>
              <Button
                onClick={() => {
                  onOpenChange(false);
                  navigate("/auth?mode=signup");
                }}
                variant="outline"
                className="flex-1"
              >
                Sign Up
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md mx-3 sm:mx-0 rounded-2xl">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-xl sm:text-2xl font-bold">Share Your Quiz</DialogTitle>
          <DialogDescription className="text-sm sm:text-base">
            Share this link with anyone. They can attempt the same quiz without logging in.
          </DialogDescription>
        </DialogHeader>

        {isGenerating ? (
          <div className="flex flex-col items-center justify-center py-8 sm:py-10 space-y-4">
            <Loader2 className="h-10 w-10 sm:h-12 sm:w-12 animate-spin text-primary" />
            <p className="text-sm sm:text-base text-muted-foreground">Generating share link...</p>
          </div>
        ) : shareLink ? (
          <div className="space-y-5 sm:space-y-6 py-2">
            <div className="space-y-3">
              <Label htmlFor="share-link" className="text-sm font-semibold">Shareable Link</Label>
              <div className="flex gap-2">
                <Input
                  id="share-link"
                  value={shareLink}
                  readOnly
                  className="flex-1 font-mono text-xs sm:text-sm bg-muted/50 border-border"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleCopy}
                  className="shrink-0 h-10 w-10"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-success" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="rounded-xl bg-gradient-to-br from-primary/10 to-accent/10 p-4 sm:p-5 space-y-3 border border-border/50">
              <p className="text-sm sm:text-base font-semibold text-foreground">What happens next?</p>
              <ul className="text-xs sm:text-sm text-foreground/80 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>Anyone can open this link and take the quiz</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>They'll see the same questions in the same order</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>Their results will be saved separately</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>Your original results remain unchanged</span>
                </li>
              </ul>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default ShareResultDialog;
